import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CheckCircle2,
  ChevronDown,
  Eye,
  FileDown,
  History,
  Loader2,
  MoreHorizontal,
} from "lucide-react";
import type { PaymentScope } from "../payment-dues.entity";

interface PaymentDueActionsProps {
  onPayViaStripe: (scope: PaymentScope) => Promise<void> | void;
  onMarkAsPaid: (scope: PaymentScope) => Promise<void> | void;
  onDownloadReport: () => Promise<void> | void;
  onViewDetails: () => void;
  onViewPaymentHistory: () => void;
  hasBothTypes?: boolean;
  markingPaid?: boolean;
  downloading?: boolean;
  payingViaStripe?: boolean;
  stripeEligible?: boolean;
  stripeUnavailableReason?: string | null;
  disabled?: boolean;
}

export default function PaymentDueActions({
  onPayViaStripe,
  onMarkAsPaid,
  onDownloadReport,
  onViewDetails,
  onViewPaymentHistory,
  hasBothTypes = false,
  markingPaid = false,
  downloading = false,
  payingViaStripe = false,
  stripeEligible = true,
  stripeUnavailableReason = null,
  disabled = false,
}: PaymentDueActionsProps) {
  const busy = useMemo(() => markingPaid || downloading || payingViaStripe, [markingPaid, downloading, payingViaStripe]);

  const handlePayClick = useCallback(async (scope: PaymentScope) => {
    if (!stripeEligible) {
      toast.error(stripeUnavailableReason ?? "Stripe Connect account not available for this recipient.");
      return;
    }
    if (payingViaStripe) return;
    try {
      await onPayViaStripe(scope);
    } catch (error: any) {
      const message = error?.message ?? "Failed to initiate Stripe payment.";
      toast.error(message);
      console.error(error);
    }
  }, [onPayViaStripe, payingViaStripe, stripeEligible, stripeUnavailableReason]);

  const handleMarkAsPaidClick = useCallback(async (scope: PaymentScope) => {
    if (markingPaid) return;
    await onMarkAsPaid(scope);
  }, [markingPaid, onMarkAsPaid]);

  const handleDownloadClick = useCallback(async () => {
    if (downloading) return;
    await onDownloadReport();
  }, [downloading, onDownloadReport]);

  // Split button: Pay (both) + dropdown for scope options
  const payGroup = (
    <div className="flex items-center">
      <Button
        size="sm"
        onClick={() => handlePayClick("both")}
        disabled={disabled || busy || !stripeEligible}
        className={hasBothTypes ? "rounded-r-none" : ""}
      >
        {payingViaStripe ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pay"}
      </Button>
      {hasBothTypes && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              disabled={disabled || busy || !stripeEligible}
              className="rounded-l-none border-l border-l-primary-foreground/20 px-1.5"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => handlePayClick("appointments")}>
              Pay Turns
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePayClick("daily_work")}>
              Pay Daily Work
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handlePayClick("both")}>
              Pay Both
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  return (
    <div className="flex items-center justify-end gap-1">
      {/*
        A disabled button swallows pointer events, so handlePayClick's toast can
        never fire for an ineligible recipient — the Pay button would otherwise
        just grey out with no stated reason. The tooltip hangs off a wrapper
        span for the same reason: the trigger must still receive those events.
      */}
      {stripeEligible ? (
        payGroup
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">{payGroup}</span>
          </TooltipTrigger>
          <TooltipContent>
            {stripeUnavailableReason ?? "Stripe Connect account not available for this recipient."}
          </TooltipContent>
        </Tooltip>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" disabled={disabled || busy} aria-label="More actions">
            {busy && !payingViaStripe ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MoreHorizontal className="h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {hasBothTypes ? (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger disabled={markingPaid}>
                {markingPaid ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                Mark as Paid
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-44">
                <DropdownMenuItem onClick={() => handleMarkAsPaidClick("appointments")}>
                  Mark Turns Paid
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleMarkAsPaidClick("daily_work")}>
                  Mark Daily Work Paid
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleMarkAsPaidClick("both")}>
                  Mark Both Paid
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : (
            <DropdownMenuItem onClick={() => handleMarkAsPaidClick("both")} disabled={markingPaid}>
              {markingPaid ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Mark as Paid
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleDownloadClick} disabled={downloading}>
            {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
            Download Report
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onViewDetails}>
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onViewPaymentHistory}>
            <History className="mr-2 h-4 w-4" />
            Payment History
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
